import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Inject,
  InjectionToken,
  Input,
  OnInit,
  Optional,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { AbstractControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { MAT_DATE_FORMATS, MatDateFormats } from '@angular/material/core';

import { DateTimeAdapter } from '../adapters/date-time-adapter';
import { RangeDateTimeAdapter } from '../adapters/range-date-time-adapter';
import { NkRangeDateTimeBase, NkRangeDateTimeConfig } from '../base/range-datetime-base';
import { NkDateTimeEdit } from '../datetime-edit/datetime-edit';
import { NkTimeEditConfig } from '../time-edit/time-edit';
import { DateTimeRange } from '../types';

type R<D> = DateTimeRange<D>;
export interface NkRangeDateTimeEditConfig extends NkRangeDateTimeConfig, NkTimeEditConfig {
  hideTime?: boolean;
}
export const NK_RANGE_DATETIME_EDIT_DEFAULT_OPTIONS = new InjectionToken<NkRangeDateTimeConfig>(
  'NK_RANGE_DATETIME_EDIT_DEFAULT_OPTIONS'
);

@Component({
  selector: 'nk-range-datetime-edit',
  templateUrl: 'range-datetime-edit.html',
  styleUrls: ['range-datetime-edit.scss'],
  host: {
    class: 'nk-range-datetime'
  },
  exportAs: 'nkRangeDateTimeEdit',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NkRangeDateTimeEdit),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => NkRangeDateTimeEdit),
      multi: true
    }
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NkRangeDateTimeEdit<D> extends NkRangeDateTimeBase<D> implements OnInit, AfterViewInit {
  constructor(
    public _dateAdapter: DateTimeAdapter<D>,
    public _rangeAdapter: RangeDateTimeAdapter<D>,
    public _changeDetectorRef: ChangeDetectorRef,
    @Optional()
    @Inject(NK_RANGE_DATETIME_EDIT_DEFAULT_OPTIONS)
    public config: NkRangeDateTimeEditConfig,
    @Inject(MAT_DATE_FORMATS) private _dateFormats: MatDateFormats
  ) {
    super(_dateAdapter, _rangeAdapter, _changeDetectorRef, config);
    // if (!this._dateFormats) {
    //   throw createMissingRangeImplError('MAT_DATE_FORMATS');
    // }
  }
  /** show/hide the time edit part, transforming the component in a simple range datepicker*/
  @Input() hideTime;

  @Input() showSeconds;
  @Input() showMilliseconds;
  @Input() showMeridian;
  @Input() startAt: { start: D | null; end: D | null };
  @Input() defaultTime: { start: string | null; end: string | null };

  /** Reference to the start range view component. */
  @ViewChild('start', { static: true }) startEdit: NkDateTimeEdit<D>;
  /** Reference to the ebd range view component. */
  @ViewChild('end', { static: true }) endEdit: NkDateTimeEdit<D>;

  /** match the range calendars with the current date  */
  goToRangeInView(range: R<D>, view: 'month' | 'year' | 'multi-year'): void {
    const viewRange = range || this.value;
    if (!this._rangeAdapter.isEmpty(viewRange)) {
      this.startEdit.calendar._goToDateInView(viewRange.start, view || 'month');
      this.endEdit.calendar._goToDateInView(viewRange.end, view || 'month');
    }
    this._changeDetectorRef.markForCheck();
  }

  updateControls = (range: R<D>) => {
    setTimeout(() => {
      // needed for updating the selected class range
      this.startEdit.calendar.updateTodaysDate();
      this.endEdit.calendar.updateTodaysDate();
    });
    this._changeDetectorRef.markForCheck();
  };

  ngOnInit(): void {
    this.config = this.config || {};
    this.hideTime = this.hideTime === void 0 ? this.config.hideTime : coerceBooleanProperty(this.hideTime);
    this.showMeridian =
      this.showMeridian === void 0 ? this.config.showMeridian : coerceBooleanProperty(this.showMeridian);
    this.showSeconds = this.showSeconds === void 0 ? this.config.showSeconds : coerceBooleanProperty(this.showSeconds);
    this.showMilliseconds =
      this.showMilliseconds === void 0 ? this.config.showMilliseconds : coerceBooleanProperty(this.showMilliseconds);
    super.ngOnInit();
  }

  ngAfterViewInit(): void {
    this.startEdit.validStateChange.subscribe((errs: ValidationErrors) => {
      this._validationErrors = this.startEdit._validationErrors;
      this._onValidatorChange();
    });
    this.endEdit.validStateChange.subscribe((errs: ValidationErrors) => {
      this._validationErrors = this.endEdit._validationErrors;
      this._onValidatorChange();
    });
  }

  validate(c: AbstractControl): ValidationErrors | null {
    this._validationErrors = this.startEdit._validationErrors || this.endEdit._validationErrors || super.validate(c);
    return this._validationErrors;
  }
}
